/* eslint-disable camelcase */

// Workflow Status Types
export type WorkflowStatus = 'draft' | 'active' | 'stopped';

// Node Types
export const WORKFLOW_NODE_TYPE = {
  // Triggers
  TRIGGER_NEW_LEAD: 'newLeadTrigger',
  TRIGGER_NEW_DONATION: 'newDonationTrigger',

  // Actions
  ACTION_TIME_DELAY: 'timeDelayAction',
  ACTION_SEND_EMAIL: 'sendEmailAction',
  ACTION_SEND_PUSH: 'sendPushNotificationAction',
} as const;

export type WorkflowNodeType = (typeof WORKFLOW_NODE_TYPE)[keyof typeof WORKFLOW_NODE_TYPE];

// Node Data Types
export type WorkflowNode = {
  id: string;
  type: string;
  data?: Record<string, unknown>;
  position?: { x: number; y: number };
  [key: string]: unknown;
};

export type WorkflowEdgeCondition = {
  result: boolean;
  [key: string]: unknown;
};

export type WorkflowEdge = {
  id: string;
  source: string; // Node ID
  target: string; // Node ID
  type?: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  data?: Record<string, unknown>;
  condition?: WorkflowEdgeCondition;
  [key: string]: unknown;
};

// Node Attribute Types
export type NewLeadTriggerAttributes = {
  lead_campaign_id: string; // UUID of lead campaign
};

export type NewDonationTriggerAttributes = {
  giving_campaign_id: string; // UUID of giving campaign
};

export type SendEmailActionAttributes = {
  email_campaign_id: string; // UUID of email campaign
};

export type TimeDelayActionAttributes = {
  value: number;
  unit: 'minutes' | 'hours' | 'days' | 'weeks';
};

export type SendPushNotificationActionAttributes = {
  title: string;
  message: string;
  action?: {
    type: 'url' | 'deeplink';
    value: string;
    content_id?: string;
    content_series_id?: string;
    offer_id?: string;
  };
};

export type WorkflowNodeAttributes =
  | NewLeadTriggerAttributes
  | NewDonationTriggerAttributes
  | SendEmailActionAttributes
  | TimeDelayActionAttributes
  | SendPushNotificationActionAttributes;

export type WorkflowCondition =
  | {
      schema_version: 1;
      operator: 'any' | 'all';
      conditions: Array<{
        predicate: 'trial_started' | 'superfunnel_signup_exists';
      }>;
    }
  | {
      schema_version: 2;
      operator: 'any' | 'all';
      conditions: Array<{
        metric:
          | 'subscription.trial_started'
          | 'lead.superfunnel_signup_exists'
          | 'lead.latest_superfunnel_platform_is_web'
          | 'subscription.active'
          | 'content_subscription.duration_days'
          | 'content_subscription.churned'
          | 'content_subscription.backfilled'
          | 'giving_subscription.active'
          | 'client.mobile_app_exists'
          | 'client.first_mobile_seen_age_days'
          | 'user.email_verified'
          | 'user.email_valid'
          | 'user.email_suppressed'
          | 'user.country_restricted'
          | 'user.account_age_days';
        operator:
          | 'equals'
          | 'not_equals'
          | 'greater_than'
          | 'greater_than_or_equal'
          | 'less_than'
          | 'less_than_or_equal';
        value: boolean | number;
      }>;
    };

export type WorkflowRecipientPolicy = {
  schema_version: 1;
  entry_condition?: WorkflowCondition;
  exit_condition?: WorkflowCondition;
  quiet_hours?: {
    start_local_time: string;
    end_local_time: string;
    delivery_policy: 'next_available_time';
  };
  email?: {
    require_verified_sender: boolean;
    require_unsubscribe_group: boolean;
  };
};

// Workflow Snapshot Structure
export type WorkflowSnapshotMetadata = {
  version?: string;
  created_at?: string;
  draft_only?: boolean;
  blocking_issues?: string[];
  deviations?: Array<{
    code: string;
    description: string;
  }>;
  recipient_policy?: WorkflowRecipientPolicy;
  [key: string]: unknown;
};

export type WorkflowSnapshot = {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  metadata?: WorkflowSnapshotMetadata;
};

// Workflow Automation Types
export type WorkflowAutomation = {
  id: string;
  name: string;
  description: string | null;
  status: WorkflowStatus;
  current_version: number | null;
  last_updated_at: string | null; // ISO date string
  enrolled: number;
  completed: number;
  is_superfunnel: boolean; // Pray-managed Superfunnel drip; read-only for everyone but a Studio admin
};

// Workflow Version Types
export type WorkflowMigrationGuard = {
  schema_version: 1;
  source_provider: 'braze';
  source_canvas_id: string;
  draft_only: boolean;
  blocking_issues: string[];
};

export type WorkflowVersion = {
  id: string;
  version: number | null; // null for drafts
  node_states: Record<
    string,
    {
      workflowKey: string;
      nodeId: string;
      lastUpdated: string;
      workflowId: string;
      workflowVersion: number;
      nodeType: string;
      executionOrder: number;
      completedCount: number;
      currentCount: number;
      currentlyWaiting: number;
    }
  >;
  workflow_snapshot: WorkflowSnapshot | string | null; // null when excluded for performance, string when returned from API
  draft_revision: number;
  snapshot_hash: string | null;
  migration_guard: WorkflowMigrationGuard | null;
  is_published: boolean;
  activated_at: string | null;
  deactivated_at: string | null;
  created_by: number;
  publish_notes: string | null;
  created_at: string;
  updated_at: string;
};

// Workflow Template Types
export type WorkflowTemplate = {
  id: string;
  name: string;
  description: string | null;
  thumbnail_url: string | null;
  workflow_snapshot: WorkflowSnapshot;
  created_at: string;
  updated_at: string;
};

// Request Types
export type CreateWorkflowRequest = {
  name: string;
  description?: string;
};

export type UpdateWorkflowRequest = {
  name?: string;
  description?: string | null;
};

export type CreateVersionRequest = {
  workflow_snapshot: WorkflowSnapshot;
  is_draft?: boolean;
  expected_draft_revision?: number;
  expected_snapshot_hash?: string | null;
  migration_guard?: WorkflowMigrationGuard | null;
};

export type DraftIdentity = Pick<WorkflowVersion, 'id' | 'draft_revision' | 'snapshot_hash'>;

export type PublishWorkflowVersionRequest = {
  version_id: string;
  expected_draft_revision: number;
  expected_snapshot_hash: string;
};

export type CloneTemplateRequest = {
  name: string;
  description?: string;
  configuration_overrides?: object;
};

/* eslint-enable camelcase */
